import { useCallback, useState } from 'preact/hooks';
import { ApiError, api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { RoadmapResponse } from '../lib/types.js';

const STATUS_OPTIONS = ['considering', 'planned', 'in-progress', 'done', 'declined'];

export function Roadmap() {
  const fetchRoadmap = useCallback(() => api.get<RoadmapResponse>('/api/roadmap'), []);
  const roadmap = useFetch(fetchRoadmap, []);
  const [pending, setPending] = useState<number | null>(null);
  const [warn, setWarn] = useState<string | null>(null);

  const items = [...(roadmap.data?.items ?? [])].sort((a, b) => b.votes - a.votes);

  const setStatus = async (id: number, status: string) => {
    setPending(id);
    setWarn(null);
    try {
      // Not in the M2 endpoint list — the server needs to add this route.
      await api.patch(`/api/admin/roadmap/${id}`, { status });
      roadmap.reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setWarn(
          'The server has no route to edit roadmap status yet (PATCH /api/admin/roadmap/:id).',
        );
      } else {
        setWarn(err instanceof ApiError ? err.message : 'Update failed.');
      }
    } finally {
      setPending(null);
    }
  };

  return (
    <div>
      <h1 class="page-title">Roadmap</h1>
      {roadmap.error && <div class="error-banner">{roadmap.error}</div>}
      {warn && <div class="error-banner">{warn}</div>}

      <div class="card">
        {items.length === 0 && !roadmap.loading && (
          <div class="empty">Nothing on the roadmap yet.</div>
        )}
        {items.length > 0 && (
          <table class="grid">
            <thead>
              <tr>
                <th>Title</th>
                <th>Votes</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.title}</td>
                  <td>{it.votes}</td>
                  <td>
                    <select
                      class="select"
                      style={{ width: 140 }}
                      value={it.status}
                      disabled={pending === it.id}
                      onChange={(e) => setStatus(it.id, (e.target as HTMLSelectElement).value)}
                    >
                      {!STATUS_OPTIONS.includes(it.status) && (
                        <option value={it.status}>{it.status}</option>
                      )}
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
